import { FC, Fragment } from 'react'

import { useNavigate } from '@tanstack/react-router'

import { Cluster, Stack, Typography } from '@forto-dev/phoenix-ui-core'
import { Indicator } from '@forto-dev/phoenix-ui-core/common'
import { Notification } from '@forto-dev/phoenix-ui-core/ship'

import { useLocalisation } from '@/utils/localisation'

import { Notification as NotificationData } from '../domain/notification.ts'
import { NotificationCenterSearchType } from '../routes'
import { formatNotificationTopline, getIconForNotificationType } from '../utils/ui'

export const NotificationItem: FC<{ notification: NotificationData; selectedID?: string; onClick?: () => void }> = ({
  notification,
  selectedID,
  onClick,
}) => {
  const topline = formatNotificationTopline(notification)
  const navigate = useNavigate()
  const { translateDate } = useLocalisation()

  return (
    <Notification
      key={notification.id}
      status={
        selectedID && selectedID === notification.id
          ? 'active'
          : notification.status === 'new'
            ? 'unread'
            : notification.status
      }
      onClick={() => {
        onClick?.()
        navigate({
          to: '/notification-center/$id',
          params: { id: notification.id },
          search: (prev) => prev as NotificationCenterSearchType,
        })
      }}
    >
      <Cluster gap={8} y="center" fullWidth>
        {getIconForNotificationType(notification.type)}
        {/*
        min-w-0 makes sure a flex item can't overflow its container, and flex-1 is to make sure it grows to fill the container if the contents are smaller.
        This div needs to take less than 100% of the cluster since the icon+gap takes up 24px, and setting width
        to 100% matches the container's width, which is greater than the available space which makes the text overflow.
        TODO(Masood): Remove this className once we have a better solution
        */}
        {/* eslint-disable-next-line react/forbid-dom-props */}
        <div className="min-w-0 flex-1">
          <Stack gap={2} fullWidth>
            <Cluster gap={4} fullWidth y="center" x="start">
              {topline?.length ? (
                topline.map((element, index) => (
                  <Fragment key={element}>
                    {index > 0 ? <Typography variant="tiny">·</Typography> : null}
                    <Typography variant="tiny" truncate>
                      {element}
                    </Typography>
                  </Fragment>
                ))
              ) : (
                <Typography variant="tiny">--</Typography>
              )}
              {/* Using ml-auto to right align, since nesting the topline text in another Cluster makes
              text truncation not work correctly
               TODO(Masood): remove className once we have a better solution */}
              {/* eslint-disable-next-line react/forbid-dom-props */}
              <div className="ml-auto pl-1">
                <Indicator value={notification.status === 'new'} />
              </div>
            </Cluster>
            <Typography variant="small-bold">{notification.title}</Typography>
            <Typography color="muted" variant="tiny">
              {translateDate('notifications_date', notification.createdAt)}
            </Typography>
          </Stack>
        </div>
      </Cluster>
    </Notification>
  )
}
